import {
  Contents,
  ContentsType,
  CourseContent,
  LearningInfo,
  RoleType,
  sellerLearningCenterApiClient,
} from '@/api/seller/serv/oec_seller_learning_center_api';
import { IsCBRegionName, ROLE_KEY, RoleTypeMap } from '@/constants';
import { ErrorCode } from '@/containers/essay-detail/constants';
import { generateUrlSearch, parseUrlSearchParams } from '@/libs/url';
import { PAGE_SIZE, PagePathV2, ViewRole } from '@/types';
import { CODE_SUCCESS } from '@features/constants/network';
import { useContextUser } from '@features/hooks';
import { getQuery } from '@i18n-ecom/lib/dist/commonjs/url';
import { i18n, useTranslation } from '@jupiter-app/plugin-i18n';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

export const menuMap = {
  [ContentsType.Faq_Details]: {
    path: PagePathV2.FAQ,
    search: '',
    queryList: {},
  },
  [ContentsType.Feature_Guide]: {
    path: PagePathV2.KnowledgeCenter,
    search: 'menu=feature',
    queryList: { menu: 'feature' },
  },
  [ContentsType.Policy_Center]: {
    path: PagePathV2.PolicyCenter,
    search: '',
    queryList: {},
  },
};

export const findPath = (
  contentsTree?: Contents[] | CourseContent[],
  id?: number,
): Array<Contents> => {
  if (!contentsTree || !id) {
    return [];
  }
  const path: any[] = [];

  function find(
    contentsTree: Contents[] | CourseContent[],
    id: number,
  ): boolean {
    for (const content of contentsTree) {
      if (
        content.content_id === id ||
        (content.sub_contents && find(content.sub_contents, id))
      ) {
        path.push(content);
        return true;
      }
    }
    return false;
  }

  find(contentsTree, id);
  return path;
};

const findSpecPoint = (contents: Contents[], contentId: number) => {
  let target;

  for (const content of contents) {
    if (content.content_id === contentId) {
      target = content;
    }
    if (content.sub_contents) {
      const res = findSpecPoint(content.sub_contents, contentId);
      if (res) {
        return res;
      }
    }
  }
  return target;
};

const findContentIdByUrlParam = (
  contents: Contents[],
  contentId: number,
  contentsIdx: number = 0,
) => {
  const res = findSpecPoint(contents, contentId);
  if (res) {
    return findContentId(res);
  } else {
    return findContentId(contents[contentsIdx]);
  }
};

export const findContentId = (contents: Contents | CourseContent) => {
  const subContents = contents?.sub_contents;
  if (Array.isArray(subContents) && subContents.length > 0) {
    return findContentId(subContents[0]);
  } else {
    return {
      contentId: contents?.content_id,
      name: (<CourseContent>contents)?.content_name,
      type: (<CourseContent>contents)?.type,
      attachment: (<CourseContent>contents)?.attachment,
      knowledge: (<CourseContent>contents)?.knowledge,
    };
  }
};

export function useFetchContents(
  category: ContentsType,
  only_directory: boolean,
  defaultRoleValue?: ViewRole,
  triggerHotTop?: number,
) {
  const { t } = useTranslation();
  const history = useHistory();
  const [user] = useContextUser();
  // status
  const [pageLoading, setPageLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(true);
  const [pageErr, setPageErr] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [updateErr, setUpdateErr] = useState(false);
  const [needLogin, setNeedLogin] = useState(false);
  const [faqTitle, setFaqTitle] = useState();
  // catalog and rendered cardList
  const [contentsTree, setContentsTree] = useState<Contents>();
  const [cardList, setCardList] = useState<Array<LearningInfo>>();
  const [curContentId, setCurContentId] = useState<string>('0');
  const [defaultOpenKeys, setDefaultOpenKeys] = useState<string[]>();

  // pagination
  const [curPageNum, setCurPageNum] = useState(1);
  const [totalPageNum, setTotalPageNum] = useState<number>(1);

  const [role, setRole] = useState<RoleType>();
  const isFetchContentNeedRole = [
    ContentsType.Faq_Details,
    ContentsType.Feature_Guide,
  ].includes(category);

  const urlContentId =
    getQuery(location.search, 'content_id')?.toString() ?? '';

  let urlRole = getQuery(location.search, ROLE_KEY)?.toString() ?? '';
  if (category === ContentsType.Feature_Guide) {
    urlRole = ['seller', 'creator'].includes(urlRole) ? urlRole : 'seller';
  }

  // get catalog
  const fetchContentTree = async (role_type?: RoleType) => {
    try {
      const req = { category, only_directory };
      if (role_type) {
        req['role_type'] = role_type;
      }
      const treeRes = await sellerLearningCenterApiClient.GetContentsTree(req);
      if (treeRes.code === CODE_SUCCESS && treeRes.data) {
        return treeRes.data?.contents;
      } else {
        setPageLoading(false);
        setPageErr(true);
      }
    } catch (e) {
      console.log(e);
      setPageLoading(false);
      setPageErr(true);
    }
  };

  // get list with role type
  const fetchCardListWithRoleType = () => {
    return async (contentId, curPage = 1) => {
      const roleType = RoleTypeMap[urlRole] ?? RoleType.Seller;
      setRole(roleType);
      return fetchCardList(contentId, curPage, roleType);
    };
  };

  const fetchCardList = async (contentId, curPage = 1, roleType?) => {
    setUpdateLoading(true);
    setUpdateErr(false);
    setNeedLogin(false);
    try {
      const res = await sellerLearningCenterApiClient.ListKnowledge({
        content_id: contentId,
        page: curPage,
        limit: PAGE_SIZE,
        role_type: roleType ?? RoleType.Seller,
      });
      setUpdateLoading(false);
      if (res.code === CODE_SUCCESS && res.data) {
        setCardList(res.data.learning_info);
        setTotalPageNum(res.data.total ?? 1);
        setEmpty(res.data.total === 0);
      } else if (res.code === ErrorCode.ContentOnlyForLogin) {
        setNeedLogin(true);
      } else {
        setCardList([]);
        setUpdateErr(true);
      }
    } catch (e) {
      console.log(e);
      setCardList([]);
      setUpdateErr(true);
      setUpdateLoading(false);
    }
  };

  // for normal contents req, policyCenter

  const defaultFetchContents = async () => {
    setUpdateLoading(true);
    const contentsData = await fetchContentTree();
    setContentsTree(contentsData);
    if (contentsData?.sub_contents) {
      setPageErr(false);
      setPageLoading(false);
    }
  };

  // for feature guide， faq
  const fetchContentsWithRole = async () => {
    setUpdateLoading(true);

    const promises = [fetchContentTree(RoleType.Seller)];
    if (!IsCBRegionName) {
      promises.push(fetchContentTree(RoleType.Creator));
    }
    const contentTreeRes = await Promise.all(promises);

    // in case crossBorder faq don't have creator
    if (contentTreeRes[1]) {
      contentTreeRes[1]['content_id_temp'] = contentTreeRes[1]?.content_id;
      contentTreeRes[1]['content_id'] = RoleType.Creator;
      contentTreeRes[1]['content_name'] = t(
        'academy_homepage_grow_role_creator',
      );
    }

    if (contentTreeRes[0]) {
      contentTreeRes[0]['content_id_temp'] = contentTreeRes[0]?.content_id;
      contentTreeRes[0]['content_id'] = RoleType.Seller;
      contentTreeRes[0]['content_name'] = t(
        'academy_homepage_grow_role_seller',
      );

      // combine creator and seller contents
      const jointContentTree = {
        content_id: 0,
        content_name: 'feature_guide',
        // in case crossBorder faq don't have creator
        sub_contents: contentTreeRes[1]?.sub_contents
          ? (contentTreeRes as Contents[])
          : ([contentTreeRes[0]] as Contents[]),
      };

      setContentsTree(jointContentTree);
      setPageLoading(false);
      setPageErr(false);
    }
  };

  const fetchDataReq = isFetchContentNeedRole
    ? fetchContentsWithRole
    : defaultFetchContents;

  const fetchData = () => {
    setPageLoading(true);
    fetchDataReq().catch(err => {
      setPageErr(true);
    });
  };

  useEffect(() => {
    fetchData();
  }, [i18n.language, urlRole, triggerHotTop]);

  const fetchDetailList = async () => {
    const jointContentTree = JSON.parse(
      JSON.stringify(contentsTree?.sub_contents ?? []),
    );
    jointContentTree[0]['content_id'] = jointContentTree[0]['content_id_temp'];
    if (jointContentTree?.length > 1) {
      jointContentTree[1]['content_id'] =
        jointContentTree[1]['content_id_temp'];
    }

    const res = findContentIdByUrlParam(
      jointContentTree as Contents[],
      Number(urlContentId),
      urlRole === 'creator' ? 1 : 0,
    );
    const customId = res?.contentId;
    const name = res?.name;

    if (String(customId) !== urlContentId) {
      const queryList = parseUrlSearchParams();
      queryList['content_id'] = customId;
      if (urlRole) {
        queryList['role'] = urlRole;
      }
      if (menuMap[category].search) {
        Object.assign(queryList, menuMap[category].queryList);
      }
      history.push({
        pathname: menuMap[category].path,
        search: generateUrlSearch(queryList),
      });
      return;
    }
    setCurContentId(String(customId));

    setFaqTitle(name);
    const keys = findPath(
      contentsTree?.sub_contents as Contents[],
      Number(customId),
    ).map(item => {
      return String(item.content_id);
    });
    setDefaultOpenKeys(keys);
    if (category === ContentsType.Policy_Center) {
      await fetchCardList(customId, curPageNum);
    } else {
      await fetchCardListWithRoleType()(customId);
    }
    // await fetchCardList(urlContentId, curPageNum);
    setPageErr(false);
    setPageLoading(false);
  };

  useEffect(() => {
    if (contentsTree?.sub_contents) {
      fetchDetailList();
    }
  }, [i18n.language, contentsTree, urlContentId]);

  return {
    role,
    urlRole,
    empty,
    pageErr,
    updateErr,
    needLogin,
    pageLoading,
    updateLoading,
    contentsTree,
    curContentId,
    setCurContentId,
    findContentIdByUrlParam,
    setFaqTitle,
    defaultOpenKeys,
    faqTitle,

    cardList,
    fetchCardList,
    fetchCardListWithRoleType,

    curPageNum,
    setCurPageNum,
    totalPageNum,
    fetchData,
  };
}
